import React, { useEffect, useState } from 'react';

export const AiAnswerLoading: React.FC = () => {
  return (
    <div className="w-full max-w-3xl mx-auto p-4 bg-white rounded-2xl shadow animate-pulse">
      <div className="h-4 bg-gray-300 rounded mb-4"></div>
      <div className="h-4 bg-gray-300 rounded mb-4"></div>
      <div className="h-4 bg-gray-300 rounded mb-4"></div>
      <div className="h-4 bg-gray-300 rounded w-2/3"></div>
    </div>
  );
};

export const ContextLoading: React.FC = () => {
  return (
    <div className="w-full mx-auto p-6 bg-white rounded-2xl shadow flex flex-col items-center text-center space-y-4">
      <div className="text-xl font-semibold text-gray-700">
        🔎 We’re searching for the best information for you...
      </div>
      <div className="flex space-x-2">
        <div className="w-3 h-3 bg-blue-400 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
        <div className="w-3 h-3 bg-blue-400 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
        <div className="w-3 h-3 bg-blue-400 rounded-full animate-bounce"></div>
      </div>
      <div className="text-gray-500 text-sm">
        Hang tight! We're looking through the specified sources to find the most
        relevant details for your question.
      </div>
      <div className="text-gray-500 text-sm">
        Even if you navigate away from this page your research will still
        continue, you can access it from the side panel once it has completed.
      </div>
    </div>
  );
};

export const AiAnswering: React.FC = () => {
  return (
    <div className="w-full mx-auto p-6 bg-white rounded-2xl shadow flex flex-col items-center text-center space-y-4">
      <div className="text-xl font-semibold text-gray-700">
        💡 We’re using the information we found to answer your question...
      </div>
      <div className="flex space-x-2">
        <div className="w-3 h-3 bg-green-400 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
        <div className="w-3 h-3 bg-green-400 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
        <div className="w-3 h-3 bg-green-400 rounded-full animate-bounce"></div>
      </div>
      <div className="text-gray-500 text-sm">
        Almost there! We’re crafting the best possible response for you.
      </div>
      <div className="text-gray-500 text-sm">
        Even if you navigate away from this page your research will still
        continue, you can access it from the side panel once it has completed.
      </div>
    </div>
  );
};

export const AiMultiAnswering: React.FC = () => {
  return (
    <div className="w-full mx-auto p-6 bg-white rounded-2xl shadow flex flex-col items-center text-center space-y-4">
      <div className="text-xl font-semibold text-gray-700">
        💡 Thinking a bit more, there is a large amount of context to process.
      </div>
      <div className="flex space-x-2">
        <div className="w-3 h-3 bg-green-400 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
        <div className="w-3 h-3 bg-green-400 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
        <div className="w-3 h-3 bg-green-400 rounded-full animate-bounce"></div>
      </div>
      <div className="text-gray-500 text-sm">
        Almost there! We’re crafting the best possible response for you.
      </div>
      <div className="text-gray-500 text-sm">
        Even if you navigate away from this page your research will still
        continue, you can access it from the side panel once it has completed.
      </div>
    </div>
  );
};

export const SearchError: React.FC = () => {
  return (
    <div className="w-full mx-auto p-6 bg-white rounded-2xl shadow flex flex-col items-center text-center space-y-4">
      <div className="text-xl font-semibold text-red-600">
        ❗ Hmm, something went wrong with your search.
      </div>
      <div className="text-gray-500 text-sm">
        We hit a snag while trying to process your request. Please try again in
        a moment.
      </div>
      <div className="text-gray-500 text-sm">
        If this keeps happening, feel free to adjust your inputs or contact
        support.
      </div>
      {/* <button
        onClick={() => window.location.reload()}
        className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition"
      >
        Try Again
      </button> */}
    </div>
  );
};

export const DeconstructingQuestions: React.FC = () => {
  return (
    <div className="w-full mx-auto p-6 bg-white rounded-2xl shadow flex flex-col items-center text-center space-y-4">
      <div className="text-xl font-semibold text-gray-700">
        💡 Breaking down the question, for better understanding...
      </div>
      <div className="flex space-x-2">
        <div className="w-3 h-3 bg-green-400 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
        <div className="w-3 h-3 bg-green-400 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
        <div className="w-3 h-3 bg-green-400 rounded-full animate-bounce"></div>
      </div>
      <div className="text-gray-500 text-sm">
        Even if you navigate away from this page your research will still
        continue, you can access it from the side panel once it has completed.
      </div>
    </div>
  );
};

export default function LazyLoadingPlaceholder() {
  const totalPlaceholders = 5;
  const [visibleCount, setVisibleCount] = useState(1);

  useEffect(() => {
    const timer = setTimeout(() => {
      setVisibleCount((prev) => {
        if (prev >= totalPlaceholders) {
          return 1; // Reset to start again
        }
        return prev + 1;
      });
    }, 500); // Delay between items

    return () => clearTimeout(timer);
  }, [visibleCount]);

  return (
    <div className="space-y-4 w-full">
      <div className="w-full bg-gray-100 rounded-lg p-4 flex items-center space-x-4 animate-pulse transition-all">
        <div className="w-12 h-12 bg-gray-300 rounded-full flex-shrink-0"></div>
        <div className="flex-1 space-y-2 ">
          {/* <div className="w-3/4 h-4 bg-gray-300 rounded"></div>
          <div className="w-1/2 h-4 bg-gray-300 rounded"></div> */}
          {[...Array(visibleCount)].map((_, i) => {
            return (
              <div key={i}>
                <div className="w-3/4 h-4 bg-gray-300 rounded p-y-2 mt-2"></div>
                <div className="w-1/2 h-4 bg-gray-300 rounded p-y-2 mt-2"></div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export const getComponent = (step: string) => {
  switch (true) {
    case step === 'Deconstructing Questions':
      return <DeconstructingQuestions />;
    case step === 'Getting Context':
      return <ContextLoading />;
    case step === 'Building Answer':
      return <AiAnswering />;
    case step === 'Multi-Answer':
      return <AiMultiAnswering />;
    case step === 'Error':
      return <SearchError />;
  }
};
